import React, { useMemo, useState, useContext } from "react";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { calculateDuration } from "@/features/archive/lib/helperFunctions";
import type { ArchiveUser } from "@/features/archive/model/archive/ArchiveUsersResponse";
import { calculateUpdateDuration } from "@/features/archive/ui/ActiveArchiveProcedures";
import { CloseActionButton } from "@/shared/ui";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import GroupIcon from "@mui/icons-material/Group";
import {
	Box,
	Chip,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

type UpdateItem = {
	CheckListId?: number;
	CheckListName?: string;
	Date?: string | Date;
	Status?: number;
	User?: ArchiveUserItem;
};

type ArchiveUserItem = ArchiveUser & {
	UserId: number;
	UserName?: string;
	UserFullName?: string;
	UserEmail?: string;
};

type StepModalItem = {
	Name?: string;
	Description?: string;
	Created?: string | Date;
	Updated?: string | Date;
	ProjectType?: string;
	Users?: ArchiveUserItem[];
	Updates?: UpdateItem[];
};

type Props = {
	open: boolean;
	handleClose: () => void;
	selectedItem: StepModalItem | null;
};

const DATE_FORMAT = "DD/MM/YY HH:mm:ss";

function parseSectionKey(checklistName?: string): string {
	// Supports: "1.1 Something" => "1"
	const s = (checklistName ?? "").trim();
	const m = s.match(/^(\d+)\.(\d+)/);
	return m?.[1] ?? "All";
}

function stageLabelFromChecklist(name?: string, id?: number) {
	const n = (name ?? "").trim();
	return n || (id ? `Checklist ${id}` : "Checklist");
}

function toMinutes(ms: number) {
	return Math.max(0, ms) / 60000;
}

function formatMinutes(mins: number) {
	if (!Number.isFinite(mins)) return "—";
	if (mins < 60) return `${Math.round(mins)}m`;
	const h = Math.floor(mins / 60);
	const m = Math.round(mins % 60);
	return m ? `${h}h ${m}m` : `${h}h`;
}

function safeUtc(dateStr?: string | Date) {
	if (!dateStr) return null;
	const d = dayjs.utc(dateStr);
	return d.isValid() ? d : null;
}

const StepModal = ({ open, handleClose, selectedItem }: Props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { timezone } = useTime();

	const updates = selectedItem?.Updates ?? [];
	const participants: ArchiveUserItem[] = selectedItem?.Users ?? [];

	const createdStr = selectedItem?.Created
		? dayjs.utc(selectedItem.Created).tz(timezone).format(DATE_FORMAT)
		: "—";
	const updatedStr = selectedItem?.Updated
		? dayjs.utc(selectedItem.Updated).tz(timezone).format(DATE_FORMAT)
		: "—";

	const totalDuration =
		selectedItem?.Updates?.length >= 2
			? calculateDuration(selectedItem.Updates)
			: null;

	/**
	 * Noise filter:
	 * Your data often has multiple updates within 1–2 seconds.
	 * If we sum those, we will get nonsense.
	 *
	 * Rule: ignore gaps < 15 seconds (0.25 min).
	 * Adjust if you want.
	 */
	const MIN_GAP_MINUTES = 0.25;

	const { sections, stageRows, firstUpdateStr, lastUpdateStr } = useMemo(() => {
		const endFallback = safeUtc(selectedItem?.Updated);

		// group updates by checklistId
		const byChecklist = new Map<number, UpdateItem[]>();
		for (const u of updates) {
			const id = Number(u.CheckListId);
			if (!id || Number.isNaN(id)) continue;
			const arr = byChecklist.get(id) ?? [];
			arr.push(u);
			byChecklist.set(id, arr);
		}

		const rows: Array<{
			checklistId: number;
			section: string;
			label: string;
			totalMinutes: number;
			updateCount: number;
			startUtc?: string;
			endUtc?: string;
		}> = [];

		let totalAll = 0;

		let globalFirst: dayjs.Dayjs | null = null;
		let globalLast: dayjs.Dayjs | null = null;

		for (const [, list] of byChecklist.entries()) {
			const sorted = list
				.slice()
				.map((x) => ({ ...x, _d: safeUtc(x.Date) }))
				.filter((x) => x._d)
				.sort((a, b) => a._d!.valueOf() - b._d!.valueOf());

			if (!sorted.length) continue;

			const checklistId = Number(sorted[0].CheckListId);
			const label = stageLabelFromChecklist(
				sorted[0].CheckListName,
				checklistId,
			);
			const section = parseSectionKey(sorted[0].CheckListName);

			let stageTotal = 0;

			for (let i = 0; i < sorted.length; i++) {
				const t0 = sorted[i]._d!;
				const t1 = sorted[i + 1]?._d ?? endFallback ?? null;

				if (!t1) continue;

				const deltaMin = toMinutes(t1.diff(t0));
				if (deltaMin < MIN_GAP_MINUTES) continue; // ignore noise
				stageTotal += deltaMin;
			}

			// track global range (for nice header)
			const stageFirst = sorted[0]._d!;
			const stageLast = sorted[sorted.length - 1]._d!;
			if (!globalFirst || stageFirst.isBefore(globalFirst))
				globalFirst = stageFirst;
			if (!globalLast || stageLast.isAfter(globalLast)) globalLast = stageLast;

			if (stageTotal > 0) {
				totalAll += stageTotal;
				rows.push({
					checklistId,
					section,
					label,
					totalMinutes: stageTotal,
					updateCount: sorted.length,
					startUtc: sorted[0].Date,
					endUtc: sorted[sorted.length - 1].Date,
				});
			}
		}

		// Sort: section then duration desc (more useful visually)
		rows.sort((a, b) => {
			if (a.section !== b.section) return a.section.localeCompare(b.section);
			return b.totalMinutes - a.totalMinutes;
		});

		const uniqueSections = Array.from(
			new Set(rows.map((r) => r.section)),
		).filter(Boolean);

		const firstUpdateStr = globalFirst
			? globalFirst.tz(timezone).format(DATE_FORMAT)
			: "—";
		const lastUpdateStr = globalLast
			? globalLast.tz(timezone).format(DATE_FORMAT)
			: "—";

		return {
			sections: uniqueSections,
			stageRows: rows,
			totalStageMinutes: totalAll,
			firstUpdateStr,
			lastUpdateStr,
		};
	}, [updates, selectedItem?.Updated, timezone]);

	const [sectionFilter, setSectionFilter] = useState<string>("All");

	const visibleStages = useMemo(() => {
		if (sectionFilter === "All") return stageRows;
		return stageRows.filter((r) => r.section === sectionFilter);
	}, [stageRows, sectionFilter]);

	const visibleTotal = useMemo(
		() => visibleStages.reduce((acc, r) => acc + r.totalMinutes, 0),
		[visibleStages],
	);

	const chartDataset = useMemo(
		() =>
			visibleStages.map((r) => ({
				stage: r.label,
				minutes: r.totalMinutes,
				count: r.updateCount,
			})),
		[visibleStages],
	);
	const stageChartHeight = useMemo(
		() =>
			isMobile
				? Math.min(480, 140 + visibleStages.length * 46)
				: Math.min(520, 160 + visibleStages.length * 34),
		[isMobile, visibleStages.length],
	);
	const tooltipMutedTextColor =
		theme.palette.mode === "dark"
			? "rgba(226,232,240,0.72)"
			: "rgba(71,85,105,0.9)";
	const tooltipShellSx = useMemo(
		() => ({
			renderMode: "html" as const,
			appendToBody: true,
			confine: false,
			enterable: true,
			transitionDuration: 0.08,
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(15, 23, 42, 0.96)"
					: "rgba(255, 255, 255, 0.98)",
			borderColor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.45 : 0.22,
			),
			borderWidth: 1,
			borderRadius: 10,
			padding: [10, 12],
			textStyle: {
				color: theme.palette.text.primary,
				fontSize: 12,
			},
			extraCssText:
				"box-shadow: 0 10px 28px rgba(2,6,23,0.26); max-width: 360px; white-space: normal; z-index: 9999; backdrop-filter: blur(2px);",
		}),
		[theme],
	);
	const stageChartOption = useMemo<echarts.EChartsOption>(() => {
		const labels = chartDataset.map((row) => row.stage);
		const minutes = chartDataset.map((row) => Number(row.minutes ?? 0));
		const counts = chartDataset.map((row) => Number(row.count ?? 0));

		return {
			backgroundColor: "transparent",
			grid: {
				left: isMobile ? 156 : 292,
				right: 14,
				top: 10,
				bottom: 24,
			},
			xAxis: {
				type: "value",
				splitLine: {
					show: true,
					lineStyle: {
						color: alpha(theme.palette.divider, 0.22),
					},
				},
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
					formatter: (value: number) => formatMinutes(Number(value)),
				},
				axisTick: { show: false },
				axisLine: { show: false },
			},
			yAxis: {
				type: "category",
				data: labels,
				axisTick: { show: false },
				axisLine: { show: false },
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
					width: isMobile ? 128 : 256,
					overflow: "truncate",
				},
			},
			tooltip: {
				...tooltipShellSx,
				trigger: "axis",
				axisPointer: {
					type: "shadow",
				},
				formatter: (params: unknown) => {
					const point = (Array.isArray(params) ? params[0] : params) as {
						dataIndex?: number;
						marker?: string;
					};
					const index = Number(point?.dataIndex ?? 0);
					const label = labels[index] ?? "";
					const duration = minutes[index] ?? 0;
					const updateCount = counts[index] ?? 0;
					return `
						<div style="max-width:320px">
							<div style="font-weight:700; margin-bottom:6px">${label}</div>
							<div style="display:flex; justify-content:space-between; gap:12px;">
								<span>${point?.marker ?? ""}Duration</span>
								<strong>${formatMinutes(duration)}</strong>
							</div>
							<div style="margin-top:6px; color:${tooltipMutedTextColor}; font-size:12px;">
								${updateCount} updates in this checklist
							</div>
						</div>
					`;
				},
			},
			series: [
				{
					name: "Duration",
					type: "bar",
					data: minutes,
					barMaxWidth: 24,
					itemStyle: {
						color: theme.palette.primary.main,
						borderRadius: [0, 7, 7, 0],
					},
					emphasis: {
						itemStyle: {
							color: theme.palette.primary.dark,
						},
					},
				},
			],
		};
	}, [chartDataset, isMobile, theme, tooltipMutedTextColor, tooltipShellSx]);

	const hasDescription = !!selectedItem?.Description?.trim();
	const participantCount = participants.length;
	const modalType = selectedItem?.ProjectType ?? "Saved";

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="md"
			PaperProps={{
				sx: {
					borderRadius: 3,
					border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.85 : 0.75)}`,
					overflow: "hidden",
					bgcolor: "background.paper",
					boxShadow:
						theme.palette.mode === "dark"
							? `0 18px 60px ${alpha(theme.palette.common.black, 0.45)}`
							: `0 18px 60px ${alpha(theme.palette.common.black, 0.14)}`,
				},
			}}
		>
			<DialogTitle
				sx={{
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2.25,
					pb: 1.5,
					bgcolor: "background.paper",
					borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
				}}
			>
				<Stack
					direction="row"
					spacing={0.75}
					flexWrap="wrap"
					useFlexGap
					sx={{ mb: 0.9, pr: 5 }}
				>
					<Chip
						size="small"
						label={GetLanguageValueAsString(translation, "Auto.Features.Archive.Ui.Components.History.StepModal.L426C13.ArchiveActivity", "Archive activity")}
						sx={{
							height: 26,
							borderRadius: 2,
							fontWeight: 700,
							bgcolor: alpha(theme.palette.primary.main, 0.08),
							color: "primary.main",
							border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
						}}
					/>
					<Chip
						size="small"
						label={modalType}
						sx={{
							height: 26,
							borderRadius: 2,
							fontWeight: 700,
							bgcolor: alpha(theme.palette.text.primary, 0.06),
							color: "text.secondary",
							border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
						}}
					/>
				</Stack>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 900,
						pr: 5,
						lineHeight: 1.2,
						letterSpacing: "-0.02em",
					}}
				>
					{selectedItem?.Name ?? "—"}
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mt: 0.8, fontWeight: 700 }}
				>
					<Translate String="StepModal.Updates">Updates:</Translate>{" "}
					{firstUpdateStr} → {lastUpdateStr}
				</Typography>

				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Archive.Ui.Components.History.StepModal.L470C17.Close", "Close")}
					onClick={handleClose}
					size="small"
					sx={{
						position: "absolute",
						right: 10,
						top: 10,
						color: "text.secondary",
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 2,
					bgcolor: "background.paper",
				}}
			>
				<Box
					sx={{
						mb: 2,
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2, minmax(0, 1fr))",
							lg: "repeat(4, minmax(0, 1fr))",
						},
						gap: 1,
					}}
				>
					{[
						{
							label: <Translate String="StepModal.Completed">Completed</Translate>,
							value: createdStr,
						},
						{
							label: (
								<Translate String="StepModal.LastUpdated">
									Last updated
								</Translate>
							),
							value: updatedStr,
						},
						{
							label: (
								<Translate String="StepModal.TotalDuration">
									Total duration
								</Translate>
							),
							value: totalDuration ?? "—",
						},
						{
							label: (
								<Translate String="StepModal.CompletedBy">Participants</Translate>
							),
							value: participantCount,
						},
					].map((item, index) => (
						<Box
							key={index}
							sx={{
								borderRadius: 2,
								border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
								bgcolor: alpha(
									theme.palette.background.default,
									theme.palette.mode === "dark" ? 0.45 : 0.8,
								),
								px: 1.25,
								py: 1,
								minWidth: 0,
							}}
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ fontWeight: 700 }}
							>
								{item.label}
							</Typography>
							<Typography variant="body2" sx={{ mt: 0.35, fontWeight: 800 }}>
								{item.value}
							</Typography>
						</Box>
					))}
				</Box>

				{/* Description */}
				{hasDescription && (
					<Box
						sx={{
							mb: 2,
							borderRadius: 2.5,
							border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
							bgcolor: "background.paper",
							p: 1.5,
						}}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
							<Translate String="StepModal.Description">Description</Translate>
						</Typography>
						<Divider sx={{ mb: 1.25 }} />
						<Box
							sx={{
								p: 1.25,
								borderRadius: 2,
								bgcolor: alpha(
									theme.palette.background.default,
									theme.palette.mode === "dark" ? 0.45 : 0.8,
								),
								color: "text.secondary",
								whiteSpace: "pre-wrap",
							}}
						>
							{selectedItem.Description}
						</Box>
					</Box>
				)}

				{/* Completed by */}
				{participants.length > 0 && (
					<Box
						sx={{
							mb: 2,
							borderRadius: 2.5,
							border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
							bgcolor: "background.paper",
							p: 1.5,
						}}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
							<Translate String="StepModal.CompletedBy">Completed by</Translate>
						</Typography>
						<Divider sx={{ mb: 1.25 }} />
						<Stack direction="row" flexWrap="wrap" gap={0.75}>
							{participants.map((u) => (
								<Chip
									key={u.UserId}
									label={u.UserFullName || u.UserName || `User ${u.UserId}`}
									size="small"
									icon={<GroupIcon sx={{ fontSize: 16 }} />}
									sx={{
										height: 26,
										borderRadius: 2,
										fontWeight: 700,
										bgcolor: alpha(theme.palette.text.primary, 0.06),
										color: "text.secondary",
										border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
										"& .MuiChip-icon": { opacity: 0.8 },
									}}
								/>
							))}
						</Stack>
					</Box>
				)}

				{/* ✅ Chart: checklist duration */}
				<Box
					sx={{
						mb: 2,
						borderRadius: 2.5,
						border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
						bgcolor: "background.paper",
						p: 1.5,
					}}
				>
					<Stack
						direction="row"
						alignItems="baseline"
						justifyContent="space-between"
						gap={1}
						sx={{ mb: 1 }}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 950 }}>
							<Translate String="StepModal.StageDurationByChecklist">
								Stage duration (by checklist)
							</Translate>
						</Typography>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ whiteSpace: "nowrap" }}
						>
							<Translate String="StepModal.TotalShown">Total shown</Translate>:{" "}
							<b>{formatMinutes(visibleTotal)}</b>
						</Typography>
					</Stack>
					<Divider sx={{ mb: 1.25 }} />

					<Box
						sx={{
							borderRadius: 2.25,
							border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.8 : 0.7)}`,
							bgcolor: alpha(
								theme.palette.background.default,
								theme.palette.mode === "dark" ? 0.4 : 0.72,
							),
							p: { xs: 1.25, sm: 1.5 },
						}}
					>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems={{ xs: "stretch", sm: "center" }}
							justifyContent="space-between"
							gap={1}
							sx={{ mb: 1.25 }}
						>
							<ToggleButtonGroup
								size="small"
								exclusive
								value={sectionFilter}
									onChange={(_, v: string | null) => v && setSectionFilter(v)}
								sx={{
									"& .MuiToggleButton-root": {
										textTransform: "none",
										fontWeight: 900,
										borderRadius: 2,
										px: 1.25,
										borderColor: `${alpha(theme.palette.divider, 0.85)} !important`,
										backgroundColor: "background.paper",
									},
									"& .MuiToggleButton-root.Mui-selected": {
										backgroundColor: alpha(theme.palette.primary.main, 0.1),
										color: "primary.main",
										borderColor: `${alpha(theme.palette.primary.main, 0.18)} !important`,
									},
								}}
							>
								<ToggleButton value="All"><Translate String="Auto.Features.Archive.Ui.Components.History.StepModal.L702C35.All">All</Translate></ToggleButton>
								{sections
									.filter((s) => s !== "All")
									.map((s) => (
										<ToggleButton key={s} value={s}>
											{s}
										</ToggleButton>
									))}
							</ToggleButtonGroup>

							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ textAlign: { xs: "left", sm: "right" } }}
							>
								<Translate String="StepModal.NoiseFilter">
									Noise filter
								</Translate>
								: <b>{Math.round(MIN_GAP_MINUTES * 60)}<Translate String="Auto.Features.Archive.Ui.Components.History.StepModal.L720C48.S">s+</Translate></b>
							</Typography>
						</Stack>

						{!visibleStages.length ? (
							<Box sx={{ py: 4, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									<Translate String="StepModal.NoStageTimingDataAvailable">
										No stage timing data available.
									</Translate>
								</Typography>
							</Box>
						) : (
							<Box sx={{ width: "100%", overflowX: "auto" }}>
								<ReactECharts
									notMerge
									opts={{ renderer: "canvas" }}
									option={stageChartOption}
									style={{
										height: stageChartHeight,
										minWidth: isMobile ? 520 : 840,
									}}
								/>
							</Box>
						)}

						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", mt: 1, opacity: 0.9 }}
						>
							<Translate String="StepModal.TimeIsCalculatedFromGapsBetweenConsecutiveUpdatesInsideEachChecklist">
								Time is calculated from gaps between consecutive updates inside
								each checklist.
							</Translate>
						</Typography>
					</Box>
				</Box>

				{/* Timeline (keep, but modern) */}
				{updates.length > 0 && (
					<Box
						sx={{
							borderRadius: 2.5,
							border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
							bgcolor: "background.paper",
							p: 1.5,
						}}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 1 }}>
							<Translate String="StepModal.ActivityTimeline">
								Activity timeline
							</Translate>
						</Typography>
						<Divider sx={{ mb: 1.25 }} />

						<Stack
							spacing={0}
							sx={{
								borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.28)}`,
								pl: 2,
								ml: 0.5,
							}}
						>
							{updates.map((u, index: number) => {
								const dateStr = u?.Date
									? dayjs.utc(u.Date).tz(timezone).format(DATE_FORMAT)
									: "—";
								const previousDate = updates[index - 1]?.Date;
								const durationStr =
									index > 0 && previousDate && u?.Date
										? calculateUpdateDuration(
												String(previousDate),
												String(u.Date),
											)
										: null;

								const isActive = u?.Status === 1;
								const who = u?.User?.UserFullName || u?.User?.UserName || "—";
								const what = stageLabelFromChecklist(
									u?.CheckListName,
									u?.CheckListId,
								);

								return (
									<Box
										key={`${u?.CheckListId ?? "x"}-${index}`}
										sx={{
											position: "relative",
											pb: 1.5,
											"&::before": {
												content: '""',
												position: "absolute",
												left: -18,
												top: 8,
												width: 10,
												height: 10,
												borderRadius: "50%",
												backgroundColor: isActive
													? theme.palette.success.main
													: theme.palette.grey[500],
												border: `2px solid ${theme.palette.background.paper}`,
												boxSizing: "border-box",
											},
										}}
									>
										<Stack
											direction="row"
											alignItems="center"
											flexWrap="wrap"
											gap={1}
											sx={{
												p: 1,
												borderRadius: 2,
												bgcolor: alpha(
													theme.palette.background.default,
													theme.palette.mode === "dark" ? 0.45 : 0.8,
												),
												border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
											}}
										>
											<Typography variant="body2" sx={{ fontWeight: 800 }}>
												{what}
											</Typography>
											<Chip
												label={isActive ? "Active" : "Inactive"}
												size="small"
												variant="outlined"
												color={isActive ? "success" : "default"}
												sx={{
													height: 24,
													fontSize: "0.72rem",
													fontWeight: 800,
													borderRadius: 2,
												}}
											/>
											<Typography variant="caption" color="text.secondary">
												• {who}
											</Typography>

											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ display: "block", width: "100%" }}
											>
												<AccessTimeIcon
													sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.25 }}
												/>
												{dateStr}
												{durationStr ? (
													<>
														{" · "}
														<Box component="span" sx={{ fontStyle: "italic" }}>
															{durationStr} since previous
														</Box>
													</>
												) : null}
											</Typography>
										</Stack>
									</Box>
								);
							})}
						</Stack>
					</Box>
				)}
			</DialogContent>

			<DialogActions
				sx={{
					px: { xs: 2, sm: 2.5 },
					py: 1.5,
					borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
					bgcolor: "background.paper",
				}}
			>
				<CloseActionButton onClick={handleClose} sx={{ borderRadius: 2 }}>
					<Translate String="Procedure.Button.Close">Close</Translate>
				</CloseActionButton>
			</DialogActions>
		</Dialog>
	);
};

export default StepModal;
